/**
 * 数据源与表的动态解析。
 *
 * 背景：报表中间层（IR）不应把「数据源」和「表/对象」硬编码死
 * （如一律假设某个数据源是 MySQL、或在 IR 里写死固定数量/固定 id）。
 * 真实环境里：
 *   - 数据源是在服务端「已配置」的，其 id / type / driver 应通过
 *     `loadDatasourceDefs` 动态获取，而不是在 IR 里写死；
 *     同一个租户下到底有哪些数据源、各是什么类型，由服务端实际配置决定，
 *     必须按实际场景让用户从 `load-datasource-defs` 返回的列表里选定，
 *     不能假设只有某几个或默认挑某一个；
 *   - 数据集 SQL 里的表/对象，应通过 `buildDatabaseTables` 确认它确实
 *     存在于该数据源下，无法确认时（表名拼错、有多个候选）要向用户确认。
 *
 * 本模块把这两步抽出来，供 generate 在 SQL 校验阶段复用，避免：
 *   1) IR 必须手填 datasource.id（强制要求，禁止自动从列表推断）；
 *   2) SQL 里写了数据库根本不存在的表却一路绿灯到预览才报错。
 */

import type { DatasourceIR, DatasetIR, ReportIR } from '../ir/types.js';
import type { Session } from '../../client/session.js';
import { loadDatasourceDefs, buildDatabaseTables, buildFields, loadBeanDatasets } from '../../client/datasourceApi.js';
import { getLogger } from '../logger.js';

const log = getLogger('core:validate');

/** 用户确认回调：给出候选与提示，返回用户的选择（或 null 表示放弃/取消）。 */
export type ConfirmFn<T> = (prompt: string, candidates: T[]) => Promise<T | null>;

export interface ResolveOptions {
  tenantId?: string;
  /** 交互确认回调（CLI 用 readline，测试可注入固定答案）。缺省为「不确认，直接报错」。 */
  confirm?: ConfirmFn<any>;
  /** 关闭数据源推断：ds.id 缺失时直接抛错，而不是尝试 list+load。 */
  skipDatasourceResolve?: boolean;
  /** 关闭表确认：表不存在时直接抛错，而不是确认。 */
  skipTableResolve?: boolean;
}

const noopConfirm: ConfirmFn<any> = async () => null;

/**
 * 解析一个数据源 IR，确保拿到真实的 `id`（kind=server）。
 *
 * 策略：
 *  - 已有 `id` 且 `kind=server`：直接返回（仍会用 loadDatasourceDefs 补全 type/datasourceType，除非 skip）。
 *  - 缺 `id`：禁止自动从数据源列表推断，**必须由调用方（LLM/用户）先用 `load-datasource-defs`
 *    拿到真实 id 并显式写入 IR**；否则直接报错。
 *
 * 返回补全后的 DatasourceIR（不修改入参）。
 */
export async function resolveDatasource(
  session: Session,
  ds: DatasourceIR,
  opts: ResolveOptions = {},
): Promise<DatasourceIR> {
  const isServer = !!ds.id;

  // 已显式给定 id 且为 server：直接 load 补全真实定义（driver/url/type）。
  // 注意：后端 loadDatasourceDefs 返回的 def.id 往往是它的内部展示名/主键别名
  // （如 basksoft_server 返回 samplecenter-jdbc），不等于「连库用的 id 令牌」。
  // 因此必须保留 IR 原始 ds.id 作为后续 buildFields/buildSchema 的连接标识，
  // 仅用 def 补全 type / datasourceType 等元信息，禁止用 def.id 覆盖。
  if (ds.id && isServer && !opts.skipDatasourceResolve) {
    const def = await safeLoad(session, ds.id, opts.tenantId);
    // def.type 是服务端数据源的权威类型标识（jdbc/api/virtual/adapt）；
    // IR 显式声明的 datasourceType 次之（用于覆盖/兜底）。
    const inferredDsType = (def.type as any) || ds.datasourceType;
    if (def) return { ...ds, kind: 'server', id: String(ds.id), type: def.type ?? ds.type, datasourceType: inferredDsType };
    return { ...ds, kind: 'server' };
  }

  if (opts.skipDatasourceResolve) {
    if (!ds.id) throw new Error(`数据源 "${ds.name}" 缺少 id（服务端数据源 ID），且已禁用自动解析。请在 IR 中显式给出 id，并用 load-datasource-defs 确认。`);
    return { ...ds, kind: 'server', id: String(ds.id) };
  }

  // 缺 id：禁止自动从数据源列表推断，必须由调用方先用 load-datasource-defs 拿到真实 id 并写入 IR。
  throw new Error(
    `数据源 "${ds.name}" 缺少 kind=server 的 id，且禁止自动从数据源列表推断。` +
    `请先用 \`datasource load-datasource-defs --datasource-id <id>\` 探查服务端已配置的数据源，` +
    `拿到真实 id（如 samplecenter-jdbc）后显式写入 IR（kind: "server" + id）。`,
  );
}

async function safeLoad(session: Session, id: string | number, tenantId?: string): Promise<any | null> {
  try {
    return await loadDatasourceDefs(session, id, { tenantId: tenantId ?? '' });
  } catch {
    return null;
  }
}

/**
 * 从 SQL 中抽取 FROM / JOIN 后的表名（粗略，足够用于「表是否存在」核对）。
 * 处理子查询、多 JOIN、带 schema 前缀（schema.table）、别名（t / (select ...) x）。
 */
export function extractTableNames(sql: string): string[] {
  const cleaned = sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
  const names = new Set<string>();
  // FROM / JOIN 之后的首个标识符（可能带 schema.），直到空白/别名/ON/( 等。
  const re = /\b(?:from|join)\s+([`"[]?)([a-zA-Z_][\w$]*)(?:\.([`"[]?[a-zA-Z_][\w$]*)[`"\]]?)?[`"\]]?/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(cleaned))) {
    const schema = m[2] ?? '';
    const table = m[3];
    if (table) names.add(`${schema}.${table}`);
    else names.add(schema);
  }
  return [...names];
}

/**
 * 确认某数据集 SQL 引用的表/对象，确实存在于其数据源下（buildDatabaseTables）。
 *
 * 返回：
 *  - unknown: 数据库里没有、且用户也未确认的表名；
 *  - resolvedTables: 已确认的表名清单（含用户强制确认的）。
 */
export async function resolveDatasetTables(
  session: Session,
  datasourceId: string,
  ds: DatasetIR,
  opts: ResolveOptions = {},
): Promise<{ unknown: string[]; confirmed: string[] }> {
  if (ds.type !== 'sql' || !ds.sql) return { unknown: [], confirmed: [] };
  const confirm = opts.confirm ?? noopConfirm;

  const refTables = extractTableNames(ds.sql);
  if (refTables.length === 0) return { unknown: [], confirmed: [] };

  let dbTables: { name: string }[] = [];
  try {
    dbTables = await buildDatabaseTables(session, datasourceId, { tenantId: opts.tenantId ?? '' });
  } catch (e) {
    // 探查失败不阻断：交由后续的 build-fields / verify-sql 兜底，这里只做「软确认」。
    log.warn(`  [warn] 列举数据源 ${datasourceId} 下表失败，跳过表存在性确认：${e instanceof Error ? e.message : e}`);
    return { unknown: [], confirmed: refTables };
  }

  const exist = new Set(dbTables.map((t) => (t.name ?? '').toLowerCase()));
  const unknown: string[] = [];
  const confirmed: string[] = [];

  for (const t of refTables) {
    if (exist.has(t.toLowerCase())) {
      confirmed.push(t);
      continue;
    }
    // 不存在：尝试给出「你是不是想找 X」近似候选。
    const lower = t.toLowerCase();
    const stem = lower.replace(/^(t|tb|tbl|tab)[_.]?/, '');
    const candidates = dbTables
      .map((d) => d.name)
      .filter((n) => n && (n.toLowerCase().includes(lower) || lower.includes(n.toLowerCase()) || (stem.length >= 3 && n.toLowerCase().includes(stem))))
      .slice(0, 5);

    if (opts.skipTableResolve) {
      unknown.push(t);
      continue;
    }
    const choice = candidates.length
      ? await confirm(`数据集 "${ds.name}" 的 SQL 引用了表 "${t}"，但在数据源 ${datasourceId} 下未找到。${candidates.length ? `你是不是想用以下之一？` : ''}`, candidates.length ? candidates : [t])
      : await confirm(`数据集 "${ds.name}" 的 SQL 引用了表 "${t}"，但在数据源 ${datasourceId} 下未找到。请确认表名是否正确：`, [t]);

    if (choice) confirmed.push(t);
    else unknown.push(t);
  }

  return { unknown, confirmed };
}

export interface DatasetFieldCheck {
  ok: boolean;
  /** IR fields[].name 中不存在于数据库真实字段集合的列（LLM 臆造列）。 */
  irFieldsMissingInDb: string[];
  /** 单元格引用了该数据集、但不在真实字段集合中的字段。 */
  unknownCellRefs: string[];
}

/**
 * 回源校验 SQL 数据集字段：用 buildFields 拿数据库真实列，与 IR 声明的 ds.fields
 * 以及单元格实际引用的字段做交叉比对。
 *
 * 背景：structureCheck 只能校验「单元格 field ∈ ds.fields」，而 ds.fields 本身
 * 可能是 LLM 臆造的。这一步把「字段必须来自真实数据库」真正落地——和 jimureport
 * 强调的「禁止臆造字段」同理，只是我们用回源硬校验而非规则约束。
 *
 * 回源失败（网络/权限）时不阻断，交由下游 verify-sql 兜底。
 */
export async function resolveDatasetFields(
  session: Session,
  datasourceId: string,
  ds: DatasetIR,
  ir: ReportIR,
  opts: ResolveOptions = {},
): Promise<DatasetFieldCheck> {
  const result: DatasetFieldCheck = { ok: true, irFieldsMissingInDb: [], unknownCellRefs: [] };
  if (ds.type !== 'sql' || !ds.sql) return result;
  try {
    const { fields } = await buildFields(session, datasourceId, ds.sql, { tenantId: opts.tenantId ?? '' });
    const real = new Set((fields ?? []).map((f) => (f.name ?? '').toLowerCase()).filter(Boolean));
    if (real.size === 0) return result; // 回源无字段，不臆断，交给下游兜底

    for (const f of ds.fields ?? []) {
      if (!real.has(f.name.toLowerCase())) result.irFieldsMissingInDb.push(f.name);
    }

    const refd = new Set<string>();
    for (const c of ir.cells ?? []) {
      if (c.content?.kind === 'dataset' && c.content.dataset === ds.name && c.content.field) {
        refd.add(c.content.field.toLowerCase());
      }
    }
    for (const f of refd) {
      if (!real.has(f)) result.unknownCellRefs.push(f);
    }

    result.ok = result.irFieldsMissingInDb.length === 0 && result.unknownCellRefs.length === 0;
  } catch {
    // 回源失败不阻断
  }
  return result;
}

/**
 * 由「数据源 id + 数据集名称」反查服务端数据集 ID（datasetId）。
 *
 * 背景：API / Adapt / Virtual 这类 server 数据源的数据集，其 datasetId 是**派生信息**，
 * 不必让 IR 持久化，也无需 LLM/用户手写。服务端 `buildDatabaseTables(datasourceType=api|adapt|virtual)`
 * 返回 `{ id, name, type }` 数组，足以按数据集名称反查 datasetId。
 *
 * 本函数复用了 SQL 数据集确认表存在性时用到的同一端点，对 server 数据源零边际网络成本。
 *
 * @param session       已鉴权会话
 * @param datasourceId  服务端数据源 ID（DatasourceIR.id）
 * @param datasetName   数据集名称（DatasetIR.name）
 * @param datasourceType 服务端数据源类型：api | adapt | virtual（决定 buildDatabaseTables 的 datasourceType）
 * @returns 命中则返回 datasetId；未命中抛错（携带候选名提示）
 */
export async function resolveDatasetId(
  session: Session,
  datasourceId: string,
  datasetName: string,
  datasourceType: string,
  opts: ResolveOptions = {},
): Promise<string> {
  const list = await buildDatabaseTables(session, datasourceId, {
    tenantId: opts.tenantId ?? '',
    datasourceType,
  }).catch(() => [] as { name?: string; id?: string; type?: string }[]);

  const target = (datasetName ?? '').toLowerCase();
  const hit = list.find((t) => (t.name ?? '').toLowerCase() === target);
  if (hit?.id != null) return String(hit.id);

  // 未命中：给出「你是不是想找 X」候选，便于快速定位拼写问题。
  const candidates = list
    .map((t) => t.name)
    .filter((n): n is string => !!n)
    .slice(0, 5);
  const hint = candidates.length
    ? `数据源 ${datasourceId} 下可用数据集：${candidates.join('、')}`
    : `数据源 ${datasourceId} 下列举不到任何数据集，请确认数据源类型与连接。`;
  throw new Error(
    `未能在数据源 ${datasourceId}（type=${datasourceType}）下反查到数据集 "${datasetName}" 的 id。${hint}`,
  );
}

export interface BeanNameResolveResult {
  ok: boolean;
  /** 未命中服务端已注册 BeanDataset 名单的数据集名（含「你是不是想写 X」候选提示）。 */
  unknown: string[];
  /** 已自动回写为服务端原始名称（大小写规范化）的记录，供调用方打印提示。 */
  renamed: Array<{ from: string; to: string }>;
}

/**
 * 回源校验 BeanDataset 名称：用 loadBeanDatasets 拿服务端已注册的 BeanDataset
 * 名单，与 IR 中 type="bean" 数据集的 name 比对。
 *
 * 背景：BeanDataset 没有 SQL，结构自检（structureCheck）又拿不到服务端注册名单，
 * 无法判断 name 是「原始名称」还是自起别名（如"自定义员工表" vs "employee"）。
 * 但序列化输出 `<dataset type="bean" name="原始名称">`，引擎按 name 匹配服务端
 * BeanDataset，别名会让报表运行时查不到数据。这里在 generate 在线阶段兜底校验。
 *
 * 命中（忽略大小写）→ 自动把 ds.name 回写为服务端原始名称，并同步所有引用
 * 该数据集的单元格（content.dataset），保证序列化一致。
 * 未命中 → 记入 unknown 并给出「你是不是想写 X」候选，由调用方决定是否阻断。
 * 回源失败（网络/权限/无 BeanDataset 接口）→ 不阻断，交由序列化原样输出。
 */
export async function resolveBeanDatasetNames(
  session: Session,
  ir: ReportIR,
  opts: ResolveOptions = {},
): Promise<BeanNameResolveResult> {
  const beans = (ir.datasets ?? []).filter((d) => d.type === 'bean');
  const result: BeanNameResolveResult = { ok: true, unknown: [], renamed: [] };
  if (beans.length === 0) return result;

  // 声明式信任标记（fromReportId）：数据集原样取自参考报表，名称已由线上报表验证。
  // 全部受信任时完全不发起名单回源（零请求）；仅存在未受信任 bean 时才拉名单核名。
  const untrusted = beans.filter((d) => !d.fromReportId);
  for (const ds of beans) {
    if (ds.fromReportId) {
      console.log(`BeanDataset "${ds.name}" 声明来自参考报表 #${ds.fromReportId}（原样复用），跳过名称回源校验。`);
    }
  }
  if (untrusted.length === 0) return result;

  let registered: string[];
  try {
    registered = await loadBeanDatasets(session, { tenantId: opts.tenantId ?? '' });
  } catch (e) {
    log.warn(`  [warn] 拉取服务端 BeanDataset 名单失败，跳过名称回源校验：${e instanceof Error ? e.message : e}`);
    return result;
  }
  const byLower = new Map<string, string>();
  for (const n of registered ?? []) if (n) byLower.set(n.toLowerCase(), n);

  for (const ds of untrusted) {
    const lower = ds.name.toLowerCase();
    const hit = byLower.get(lower);
    if (hit) {
      if (hit !== ds.name) {
        // 大小写不一致：以服务端原始名称为准回写，并同步单元格引用。
        const oldName = ds.name;
        for (const c of ir.cells ?? []) {
          const content = c.content;
          if (content?.kind === 'dataset' && content.dataset === oldName) {
            content.dataset = hit;
          }
        }
        ds.name = hit;
        result.renamed.push({ from: oldName, to: hit });
      }
      continue;
    }
    const suggest = suggestBeanName(ds.name, registered ?? []);
    result.unknown.push(suggest ? `${ds.name}（你是不是想写「${suggest}」？）` : ds.name);
    result.ok = false;
  }
  return result;
}

/** 在候选里找编辑距离 ≤ 2 的最接近名称（用于「你是不是想写 X」提示）。 */
function suggestBeanName(target: string, candidates: string[]): string | undefined {
  const t = target.toLowerCase();
  let best: string | undefined;
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = editDistance(t, c.toLowerCase());
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return bestDist <= 2 ? best : undefined;
}

/** 经典 Levenshtein 编辑距离（供名称近似匹配用）。 */
function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Array<number>(n + 1).fill(0);
  let curr = new Array<number>(n + 1).fill(0);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    // 显式初始化当前行首格，避免依赖内层从左到右的填充顺序
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        // 字符相同：替代代价为 prev[j-1]，恒为三项最小值，直接取用
        curr[j] = prev[j - 1]!;
      } else {
        curr[j] = Math.min(prev[j]! + 1, curr[j - 1]! + 1, prev[j - 1]! + 1);
      }
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n]!;
}
